import { Box, Button } from "@mui/material";

import React, {
  CSSProperties,
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
  useEffect,
  useCallback,
} from "react";
import DocumentEditor, {
  Handle as DocumentHandle,
} from "components/editor/DocumentEditor";
import SimpleRichEditor, {
  Handle as SimpleHandle,
} from "components/editor/SimpleRichEditor";
import { useEditNote } from "services/notes";
import { Note } from "protogen/notes_pb";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import useAutoSave from "components/editor/useAutoSave";
import { InsertableSuggestion } from "components/editor/extensions/utils";
import VersionErrorAlert from "components/documents/VersionErrorAlert";

const _toMaybeNumber = (n: BigInt | number | null) => {
  if (!n) return null;
  if (typeof n === "number") return n;
  return Number(n);
};

interface Props {
  title?: string; // Only needed for tabbed panels where this is used to infer tab name.g
  note: Note;
  initialContent?: string | null;
  action?: React.ReactNode;
  style?: CSSProperties;
  summarizer?: () => void;
  onUpdate?: (n: Note) => void;
  editorType?: "simple" | "document";
  primaryAction?: React.ReactNode;
  placeholder?: string;
  setSavingInProgress?: (savingInProgress: boolean) => void;
  editorMinHeight?: string;
  editorMaxHeight?: string;
  scrollable?: boolean;
}
export type Handle = {
  insertSuggestion: (s: InsertableSuggestion) => void;
  forceSave: () => Promise<void>;
  isDirty: boolean;
};

export default forwardRef<Handle, Props>(
  (
    {
      initialContent = null,
      action,
      style,
      note,
      summarizer,
      onUpdate,
      primaryAction,
      placeholder,
      editorType = "document",
      setSavingInProgress,
      editorMinHeight,
      editorMaxHeight,
      scrollable,
    }: Props,
    ref,
  ) => {
    const _initialContent = note.content?.payload
      ? JSON.parse(note.content?.payload)
      : initialContent;
    const [hover, setHover] = useState(false);
    const documentEditorRef = useRef<DocumentHandle | null>(null);
    const simpleEditorRef = useRef<SimpleHandle | null>(null);

    // Keep track of the current hash to ensure we always use the most recent one
    const currentHashRef = useRef<string>(note.hash || "");

    // Update hash ref whenever note changes
    useEffect(() => {
      currentHashRef.current = note.hash || "";
    }, [note.hash]);

    // Function to get the current hash
    const getCurrentHash = useCallback(() => currentHashRef.current, []);

    const { request, error } = useEditNote((r) => {
      onUpdate && onUpdate(r.note);
      if (documentEditorRef.current) {
        documentEditorRef.current.updateAttachments(
          r.note?.content?.attachments || [],
        );
      }
      // Update the current hash ref with the new hash from the response
      currentHashRef.current = r.note?.hash || "";
    });

    const {
      setEditorContent,
      savingIndicator,
      savingInProgress,
      forceSave,
      isDirty,
    } = useAutoSave(
      note.content?.payload || null,
      _toMaybeNumber(note?.lastEditSec),
      async (c) => {
        await request({
          entityType: note.entityType,
          entityRef: note.entityRef,
          prevHash: getCurrentHash(),
          content: {
            contentType: "json",
            payload: c.json,
            textContent: c.text,
            attachments: c.attachments,
          },
        });
      },
      !!error,
    );

    const isVersionError =
      error && typeof error === "string" && error.includes("NoteVersionError");

    useImperativeHandle(ref, () => ({
      insertSuggestion: (s: InsertableSuggestion) => {
        if (editorType === "simple") {
          simpleEditorRef.current?.insertSuggestion(s);
        } else {
          documentEditorRef.current?.insertSuggestion(s);
        }
      },
      forceSave,
      isDirty,
    }));

    useEffect(() => {
      setSavingInProgress && setSavingInProgress(savingInProgress);
    }, [savingInProgress]);

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: "16px",
          alignSelf: "stretch",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            gap: "20px",
            borderRadius: "12px",
            padding: "24px",
            border: "1px solid #EAECF0",
            background: "#FFF",
            boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
            width: "100%",
            ...(hover ? { backgroundColor: "#e8f4f8" } : {}),
            ...style,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "flex-start",
                gap: "4px",
                flex: "1 0 0",
                alignSelf: "stretch",
              }}
            ></Box>
            <Box sx={{}}>{action}</Box>
          </Box>
          {editorType === "simple" && (
            <SimpleRichEditor
              ref={simpleEditorRef}
              forceOpen={true}
              placeholder={placeholder || "Write a note..."}
              setContent={setEditorContent}
              initialContent={_initialContent}
              initialAttachments={note.content?.attachments}
              primaryAction={
                primaryAction || (savingInProgress && savingIndicator)
              }
              editorMinHeight={editorMinHeight}
              editorMaxHeight={editorMaxHeight}
              scrollable={scrollable}
            />
          )}

          {(_initialContent || !summarizer) && editorType === "document" && (
            <DocumentEditor
              ref={documentEditorRef}
              setContent={setEditorContent}
              passiveEditor={true}
              placeholder={placeholder || "Write a note..."}
              initialContent={_initialContent}
              primaryAction={savingIndicator}
              attachmentsEnabled={true}
              initialAttachments={note.content?.attachments}
              setDragState={(d) => setHover(d)}
              linkUnfurlEnabled={true}
              editorMinHeight={editorMinHeight}
              editorMaxHeight={editorMaxHeight}
              scrollable={scrollable}
            />
          )}
          {/* Version Error Alert */}
          <VersionErrorAlert
            absolutePosition={false}
            show={!!isVersionError}
            editorRef={documentEditorRef}
          />
          {!_initialContent && summarizer && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                height: "130px",
                alignItems: "center",
              }}
            >
              <Box>
                <Button
                  variant="outlined"
                  startIcon={<SmartToyIcon />}
                  sx={{
                    maxWidth: "200px",
                    textTransform: "none",
                  }}
                  onClick={summarizer}
                >
                  Generate Summary
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    );
  },
);
